import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, ExternalLink, Award, Users, Brain, Target, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const STUDIES = [
  {
    id: 'bernier-2009',
    authors: 'Bernier, M., Thienot, E., Codron, R., & Fournier, J.F.',
    year: 2009,
    title: 'Mindfulness and Acceptance Approaches in Sport Performance',
    journal: 'Journal of Clinical Sport Psychology, 4(4), 320-333',
    sport: 'Golf',
    finding: 'All seven elite golfers in the mindfulness-acceptance group improved their national rankings after one year of competition, compared to only two of six in the control group receiving traditional psychological skills training.',
    quote: 'Golfers reported being more mindful of bodily sensations and accepting them, leading to better anticipation and adaptation in games.',
    link: 'https://passionatecoach.com/wp-content/uploads/2018/07/Mindfullness-in-Sport-Performance-Bernier-et-al.-2009.pdf',
    highlight: true,
  },
  {
    id: 'lutkenhouse-2007',
    authors: 'Lutkenhouse, J.M., Gardner, F.L., & Moore, Z.E.',
    year: 2007,
    title: 'A Randomized Controlled Trial comparing the Performance Enhancement Effects of Mindfulness-Acceptance-Commitment (MAC) Performance Enhancement and Psychological Skills Training Procedures',
    journal: 'Unpublished manuscript, La Salle University',
    sport: 'Multiple collegiate sports',
    finding: 'In the largest controlled MAC study to date (n=118 collegiate athletes), the MAC group showed superior coach-rated athletic performance, reduced experiential avoidance, and increased flow states compared to the traditional PST group.',
    quote: 'MAC yielded superior coach-rated performance, reduced experiential avoidance, and increased flow versus traditional PST.',
    link: null,
  },
  {
    id: 'dehghani-2018',
    authors: 'Dehghani, M., Saf, A.D., Vosoughi, A., Tebbenouri, G., & Zarnagh, H.G.',
    year: 2018,
    title: 'Effectiveness of the Mindfulness-Acceptance-Commitment-Based Approach on Athletic Performance and Sports Competition Anxiety',
    journal: 'Electronic Physician, 10(5), 6749-6755',
    sport: 'Basketball',
    finding: 'An 8-session MAC intervention significantly increased athletic performance while reducing sports competition anxiety and experiential avoidance in student-athletes with moderate effect sizes.',
    quote: 'The MAC approach significantly reduced sports competition anxiety and experiential avoidance, improving overall athletic performance in basketball players.',
    link: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC6033134/',
  },
  {
    id: 'gross-2018',
    authors: 'Gross, M., Moore, Z.E., Gardner, F.L., Wolanin, A.T., Pess, R., & Marks, D.R.',
    year: 2018,
    title: 'An Empirical Examination Comparing the Mindfulness-Acceptance-Commitment Approach and Psychological Skills Training for the Mental Health and Sport Performance of Female Student Athletes',
    journal: 'International Journal of Sport and Exercise Psychology, 16(4)',
    sport: 'Field Hockey & Volleyball',
    finding: 'Both MAC and PST groups showed performance gains, but MAC uniquely improved psychological flexibility, mindfulness, and experiential acceptance — the underlying mechanisms that sustain long-term performance under pressure.',
    quote: 'MAC uniquely improved psychological flexibility and mindfulness — the mechanisms that sustain performance under pressure.',
    link: null,
  },
  {
    id: 'zadeh-2023',
    authors: 'Zadeh, M.H., Malihi, Z.A., & Falahati, M.',
    year: 2023,
    title: 'Effects of the Mindfulness-Acceptance-Commitment (MAC) Approach on Cognitive Flexibility, Performance, and Emotion Regulation in Elite Beach Soccer Players',
    journal: 'Journal of Clinical Sport Psychology',
    sport: 'Beach Soccer',
    finding: 'A 7-week MAC programme improved cognitive flexibility, sports performance, emotion regulation, and sports mindfulness in elite athletes, with effects maintained at 2-month follow-up.',
    quote: 'The MAC intervention outperformed traditional PST indirectly via better emotion regulation, with all gains maintained at follow-up.',
    link: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10487324/',
  },
  {
    id: 'schwanhausser-2009',
    authors: 'Schwanhausser, L.',
    year: 2009,
    title: 'Application of the Mindfulness-Acceptance-Commitment (MAC) Protocol with an Adolescent Springboard Diver',
    journal: 'Journal of Clinical Sport Psychology, 3(4), 377-395',
    sport: 'Springboard Diving',
    finding: 'MAC protocol applied to a sub-elite adolescent diver produced improvements in both competitive performance scores and ratings of mindfulness, suggesting the approach is effective even with younger athletes.',
    quote: 'Results supported the use of MAC with young athletes, showing improvements in mindfulness, flow, and competitive dive performance.',
    link: null,
  },
  {
    id: 'goodman-2014',
    authors: 'Goodman, F.R., Kashdan, T.B., Mallard, T.T., & Schumann, M.',
    year: 2014,
    title: 'A Brief Mindfulness and Yoga Intervention with an Entire NCAA Division I Athletic Team',
    journal: 'Psychology of Consciousness: Theory, Research, and Practice, 1(4)',
    sport: 'NCAA Division I Athletics',
    finding: 'Mindfulness and acceptance-based intervention increased athletes\' trait mindfulness and goal-directed energy, and greater increases in mindfulness predicted better athletic performance over the competitive season.',
    quote: 'Greater increases in mindfulness predicted improvements in athletic performance over the entire competitive season.',
    link: null,
  },
];

const KEY_QUOTES = [
  {
    text: 'Optimal performance does not require the reduction or control of internal states but rather a non-judging, moment-to-moment awareness and acceptance of one\'s internal state, whatever that may be.',
    author: 'Gardner & Moore',
    source: 'Theoretical & Empirical Developments of MAC, 2012',
  },
  {
    text: 'The correctly applied, manualized MAC protocol is considered a first-line option for performance enhancement with high-level competitive athletes.',
    author: 'Gardner & Moore',
    source: 'Clinical Sport Psychology, 2006',
  },
  {
    text: 'Efforts to control or suppress unwanted thoughts and emotions may paradoxically increase their frequency and intensity, impairing the very performance athletes seek to protect.',
    author: 'Inspired by Wegner\'s Ironic Process Theory, 1994',
    source: 'Foundational principle of MAC',
  },
];

const MACResearchPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-[#2C5530] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link
              to="/#mac-section"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <p className="text-white/60 text-sm uppercase tracking-[0.2em] mb-4">The Science Behind</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6">
              The QMG Blueprint
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              The QMG Blueprint (Awareness · Acceptance · Action) is built on the Mindfulness-Acceptance-Commitment (MAC) approach developed by Frank L. Gardner and Zella E. Moore in 2001 — an evidence-based psychological framework for enhancing athletic performance. Here is what the research says.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* Key Principle */}
          <section className="py-12 md:py-16" data-testid="key-principle-section">
            <div className="bg-[#F2F0E9] border-l-4 border-[#BC562B] p-6 md:p-10">
              <p className="text-[#BC562B] text-sm uppercase tracking-[0.15em] mb-4">Core Principle</p>
              <blockquote className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] leading-relaxed mb-4">
                "{KEY_QUOTES[0].text}"
              </blockquote>
              <p className="text-[#737373] text-sm">
                — {KEY_QUOTES[0].author}, <em>{KEY_QUOTES[0].source}</em>
              </p>
            </div>
          </section>

          {/* Why MAC Works - Summary Stats */}
          <section className="pb-12 md:pb-16" data-testid="stats-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-8">Why the Evidence Matters</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: BookOpen, stat: '7+', label: 'Peer-reviewed studies' },
                { icon: Users, stat: '200+', label: 'Athletes studied' },
                { icon: Award, stat: '8', label: 'Sports validated' },
                { icon: TrendingUp, stat: '20+', label: 'Years of research' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] p-4 md:p-6 text-center" data-testid={`stat-card-${i}`}>
                  <item.icon className="w-6 h-6 text-[#2C5530] mx-auto mb-3" />
                  <p className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] font-medium">{item.stat}</p>
                  <p className="text-xs md:text-sm text-[#737373] mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Featured Quotes */}
          <section className="pb-12 md:pb-16" data-testid="quotes-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-8">What Researchers Say</h2>
            <div className="space-y-6">
              {KEY_QUOTES.slice(1).map((q, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] p-6 md:p-8">
                  <blockquote className="text-[#1A1A1A] text-base md:text-lg leading-relaxed mb-4 italic">
                    "{q.text}"
                  </blockquote>
                  <p className="text-sm text-[#737373]">
                    — {q.author}, <em>{q.source}</em>
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Golf-Specific Highlight */}
          <section className="pb-12 md:pb-16" data-testid="golf-highlight-section">
            <div className="bg-[#2C5530] p-6 md:p-10 text-white">
              <div className="flex items-start gap-4">
                <Target className="w-8 h-8 text-white/60 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white/60 text-sm uppercase tracking-[0.15em] mb-3">Golf-Specific Evidence</p>
                  <h3 className="font-['Playfair_Display'] text-xl md:text-2xl mb-4 text-white">
                    Bernier et al. (2009) — Elite Golfers
                  </h3>
                  <p className="text-white/90 leading-relaxed mb-4">
                    In a landmark study on young elite golfers at a French national training institute, <strong>all seven golfers</strong> who received the mindfulness-acceptance intervention <strong>improved their national rankings</strong> after one year of competition. In the control group receiving traditional psychological skills training, only two of six improved.
                  </p>
                  <blockquote className="border-l-2 border-white/40 pl-4 text-white/80 italic mb-4">
                    "Golfers reported being more mindful of bodily sensations and accepting them, leading to better anticipation and adaptation in games."
                  </blockquote>
                  <p className="text-white/60 text-sm">
                    Journal of Clinical Sport Psychology, 4(4), 320-333
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How This Applies - Connection to QMG */}
          <section className="pb-12 md:pb-16" data-testid="how-applies-section">
            <div className="bg-[#F2F0E9] p-6 md:p-10">
              <div className="flex items-start gap-4">
                <Brain className="w-8 h-8 text-[#BC562B] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-4">
                    How This Applies to Your Game
                  </h2>
                  <p className="text-[#57534E] leading-relaxed mb-4">
                    At Quiet Mind Golf, the QMG Blueprint isn't abstract theory — it's woven into every session log, diary entry, and performance scorecard you create. When you check "Awareness," "Acceptance," or "Action" in your diary, you're actively training the same psychological skills that these studies prove enhance performance.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4 mt-6">
                    <Link to="/mac/mindful" className="bg-white p-4 border border-[#E7E5E4] hover:border-[#2C5530]/40 hover:shadow-sm transition-all group">
                      <p className="text-[#2C5530] font-semibold text-sm mb-1">Step Back — Awareness</p>
                      <p className="text-[#57534E] text-sm mb-2">Notice your internal state without being hijacked by it. Research shows this alone reduces performance anxiety.</p>
                      <span className="text-[#2C5530] text-xs font-semibold flex items-center gap-1">Explore skills →</span>
                    </Link>
                    <Link to="/mac/accepting" className="bg-white p-4 border border-[#E7E5E4] hover:border-[#BC562B]/40 hover:shadow-sm transition-all group">
                      <p className="text-[#BC562B] font-semibold text-sm mb-1">Step Up — Acceptance</p>
                      <p className="text-[#57534E] text-sm mb-2">Accept discomfort as normal. Studies confirm this reduces experiential avoidance — the #1 predictor of choking under pressure.</p>
                      <span className="text-[#BC562B] text-xs font-semibold flex items-center gap-1">Explore skills →</span>
                    </Link>
                    <Link to="/mac/commitment" className="bg-white p-4 border border-[#E7E5E4] hover:border-[#1A1A1A]/30 hover:shadow-sm transition-all group">
                      <p className="text-[#1A1A1A] font-semibold text-sm mb-1">Step In — Action</p>
                      <p className="text-[#57534E] text-sm mb-2">Act in service of your values and process. The research shows values-driven action produces sustained performance gains.</p>
                      <span className="text-[#1A1A1A] text-xs font-semibold flex items-center gap-1">Explore skills →</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Studies List */}
          <section className="pb-12 md:pb-16" data-testid="studies-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Research Studies</h2>
            <p className="text-[#57534E] mb-8">Key studies supporting the MAC approach across different sports and athlete populations.</p>

            <div className="space-y-6">
              {STUDIES.map((study) => (
                <div
                  key={study.id}
                  className={`bg-white border p-5 md:p-6 ${study.highlight ? 'border-[#2C5530] border-l-4' : 'border-[#E7E5E4]'}`}
                  data-testid={`study-${study.id}`}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 bg-[#2C5530]/10 text-[#2C5530] text-xs font-semibold">{study.sport}</span>
                    <span className="text-xs text-[#737373]">{study.year}</span>
                    {study.highlight && (
                      <span className="px-2 py-0.5 bg-[#BC562B]/10 text-[#BC562B] text-xs font-semibold">Golf-Specific</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A] text-sm md:text-base mb-1">{study.title}</h3>
                  <p className="text-xs text-[#737373] mb-3">{study.authors} — <em>{study.journal}</em></p>
                  <p className="text-[#57534E] text-sm leading-relaxed mb-3">{study.finding}</p>
                  <blockquote className="border-l-2 border-[#E7E5E4] pl-3 text-sm text-[#57534E] italic mb-3">
                    "{study.quote}"
                  </blockquote>
                  {study.link && (
                    <a
                      href={study.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-[#2C5530] hover:underline"
                      data-testid={`study-link-${study.id}`}
                    >
                      Read full study <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="pb-8" data-testid="cta-section">
            <div className="bg-[#1A1A1A] p-8 md:p-12 text-center">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white mb-4">
                Ready to Apply the Science?
              </h2>
              <p className="text-white/70 mb-8 max-w-lg mx-auto">
                Start tracking your mental game with the QMG Blueprint built into every session, diary entry, and scorecard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="px-8 py-3 bg-[#BC562B] text-white font-semibold hover:bg-[#9A4520] transition-colors"
                  data-testid="cta-register"
                >
                  Start Free Account
                </Link>
                <Link
                  to="/the-approach"
                  className="px-8 py-3 border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors"
                  data-testid="cta-explore-approach"
                >
                  Explore The Mind Factor
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MACResearchPage;
